/**
 * Greedy meshing — the TS port of engine plan step 47.1's collision-generation
 * algorithm. The engine merges solid tiles into maximal rectangles per chunk
 * before creating Box2D fixtures (avoids a fixture-per-tile explosion and the
 * seam-snagging "ghost collision" problem between adjacent tile boxes). The
 * web editor has no physics runtime, but running the SAME algorithm here
 * serves two purposes (_docs/tilemap/05 §7, _docs/tilemap/06 §2):
 *
 *   1. A collision-preview overlay — authors see what the engine will
 *      generate ("a few merged rects, not per-tile confetti") before export.
 *   2. An export hint — `collision_rects` travel in the manifest so an engine
 *      importer can cross-check or skip re-meshing (advisory only; the
 *      engine's own mesher remains authoritative).
 *
 * Algorithm (identical shape to engine 47.1's `GreedyMeshChunk`): for each
 * unconsumed solid cell, grow a run to the right, then grow that whole strip
 * downward as far as every cell below is also solid and unconsumed; mark the
 * resulting rectangle consumed; repeat until the chunk is covered.
 */

import { CHUNK_SIZE, EMPTY_TILE, type TileID } from './constants';

export interface TileRect {
    x: number; // local column within the chunk (0..CHUNK_SIZE-1)
    y: number; // local row within the chunk (0..CHUNK_SIZE-1)
    w: number;
    h: number;
}

/**
 * Merge a chunk's solid tiles into maximal rectangles. `tiles` is the chunk's
 * flat CHUNK_AREA array (row-major); `isSolid` classifies a non-empty tile id
 * as solid (the tileset's per-tile `Solid` metadata, engine 45.1).
 */
export function greedyMeshChunk(tiles: ArrayLike<TileID>, isSolid: (id: TileID) => boolean): TileRect[] {
    const solid = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);
    for (let i = 0; i < solid.length; i++) {
        const id = tiles[i];
        solid[i] = id !== EMPTY_TILE && isSolid(id) ? 1 : 0;
    }
    const consumed = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);

    const rects: TileRect[] = [];
    for (let y = 0; y < CHUNK_SIZE; y++) {
        for (let x = 0; x < CHUNK_SIZE; x++) {
            const idx = y * CHUNK_SIZE + x;
            if (!solid[idx] || consumed[idx]) continue;

            // Grow right.
            let w = 1;
            while (x + w < CHUNK_SIZE && solid[y * CHUNK_SIZE + x + w] && !consumed[y * CHUNK_SIZE + x + w]) w++;

            // Grow the full w-wide strip down as long as every cell in the
            // next row (within [x, x+w)) is solid and unconsumed.
            let h = 1;
            while (y + h < CHUNK_SIZE && rowSpanSolid(solid, consumed, x, y + h, w)) h++;

            markConsumed(consumed, x, y, w, h);
            rects.push({ x, y, w, h });
        }
    }
    return rects;
}

function rowSpanSolid(solid: Uint8Array, consumed: Uint8Array, x: number, y: number, w: number): boolean {
    for (let i = 0; i < w; i++) {
        const idx = y * CHUNK_SIZE + x + i;
        if (!solid[idx] || consumed[idx]) return false;
    }
    return true;
}

function markConsumed(consumed: Uint8Array, x: number, y: number, w: number, h: number): void {
    for (let dy = 0; dy < h; dy++) {
        for (let dx = 0; dx < w; dx++) {
            consumed[(y + dy) * CHUNK_SIZE + x + dx] = 1;
        }
    }
}
